import { useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, tokenStorage } from '../lib/api';
import Layout from '../components/Layout';

const API_BASE = import.meta.env.DEV 
  ? 'http://localhost:8787' 
  : 'https://reactor-jarvis-app.valleyintest.workers.dev';

interface Contact {
  id: string;
  email: string;
  name: string;
  company?: string;
  position?: string;
  relationship?: string;
  notes?: string;
  isVerified: boolean;
  createdAt: string;
}

// 知人リストAPI
const contactsApi = {
  list: async (): Promise<Contact[]> => {
    const token = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/contacts`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, 'Failed to fetch contacts');
    return res.json();
  },
  create: async (data: Omit<Contact, 'id' | 'createdAt' | 'isVerified'>): Promise<Contact> => {
    const token = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/contacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, 'Failed to create contact');
    return res.json();
  },
  update: async (id: string, data: Partial<Contact>): Promise<Contact> => {
    const token = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/contacts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new ApiError(res.status, 'Failed to update contact');
    return res.json();
  },
  delete: async (id: string): Promise<void> => {
    const token = tokenStorage.get();
    const res = await fetch(`${API_BASE}/api/contacts/${id}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete contact');
  },
};

export default function ContactManagement() {
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    position: '',
    relationship: '',
    notes: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.list,
  });

  const createMutation = useMutation({
    mutationFn: contactsApi.create,
    onSuccess: () => {
      setSuccess('知人を追加しました');
      setIsAddModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : '追加に失敗しました');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Contact> }) =>
      contactsApi.update(id, data),
    onSuccess: () => {
      setSuccess('知人情報を更新しました');
      setIsEditModalOpen(false);
      setSelectedContact(null);
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: contactsApi.delete,
    onSuccess: () => {
      setSuccess('知人を削除しました');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : '削除に失敗しました');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      position: '',
      relationship: '',
      notes: '',
    });
  };

  const handleAdd = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    createMutation.mutate(formData);
  };

  const handleEdit = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;
    setError('');
    updateMutation.mutate({
      id: selectedContact.id,
      data: {
        name: formData.name,
        email: formData.email,
        company: formData.company || undefined,
        position: formData.position || undefined,
        relationship: formData.relationship || undefined,
        notes: formData.notes || undefined,
      },
    });
  };

  const openEditModal = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      company: contact.company || '',
      position: contact.position || '',
      relationship: contact.relationship || '',
      notes: contact.notes || '',
    });
    setIsEditModalOpen(true);
  };

  const filteredContacts = contacts?.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* ヘッダー */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              知人リスト管理
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              マスターの知人・取引先を管理します（KYC用）
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsAddModalOpen(true);
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            ➕ 知人を追加
          </button>
        </div>

        {/* アラート */}
        {success && (
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600">
            {success}
            <button onClick={() => setSuccess('')} className="float-right">×</button>
          </div>
        )}
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500">
            {error}
            <button onClick={() => setError('')} className="float-right">×</button>
          </div>
        )}

        {/* 検索 */}
        <div className="card">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="名前、メール、会社名で検索..."
              className="input pl-10"
            />
          </div>
        </div>

        {/* 知人一覧 */}
        <div className="card">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-2">📇</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                {searchQuery ? '検索結果がありません' : '知人リストが空です'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      名前
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      メール
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      会社
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      関係性
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      ステータス
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      アクション
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((contact) => (
                    <tr
                      key={contact.id}
                      className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-jarvis-gold/20 text-jarvis-gold flex items-center justify-center font-bold">
                            {contact.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {contact.name}
                            </p>
                            {contact.position && (
                              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                {contact.position}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4" style={{ color: 'var(--text-secondary)' }}>
                        {contact.email}
                      </td>
                      <td className="py-3 px-4" style={{ color: 'var(--text-secondary)' }}>
                        {contact.company || '-'}
                      </td>
                      <td className="py-3 px-4" style={{ color: 'var(--text-secondary)' }}>
                        {contact.relationship || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          contact.isVerified
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {contact.isVerified ? '確認済み' : '未確認'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        <button
                          onClick={() => openEditModal(contact)}
                          className="text-sm text-jarvis-gold hover:underline"
                        >
                          編集
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`${contact.name}を削除しますか？`)) {
                              deleteMutation.mutate(contact.id);
                            }
                          }}
                          className="text-sm text-red-500 hover:underline"
                        >
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 追加モーダル */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="card w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                知人を追加
              </h2>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      名前 *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      メール *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      会社名
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      役職
                    </label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    マスターとの関係性
                  </label>
                  <input
                    type="text"
                    value={formData.relationship}
                    onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                    className="input"
                    placeholder="例: 取引先、友人、元同僚"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    備考
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="btn-outline"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="btn-primary"
                  >
                    {createMutation.isPending ? '追加中...' : '追加'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 編集モーダル */}
        {isEditModalOpen && selectedContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="card w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                知人を編集
              </h2>
              <form onSubmit={handleEdit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      名前 *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      メール *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      会社名
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                      役職
                    </label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    マスターとの関係性
                  </label>
                  <input
                    type="text"
                    value={formData.relationship}
                    onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    備考
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedContact(null);
                    }}
                    className="btn-outline"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="btn-primary"
                  >
                    {updateMutation.isPending ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

